import React from 'react';
import { cn } from '../../lib/utils';
import type { StatusTone } from './StatusLabel';

export type BadgeSize = 'xs' | 'sm' | 'md';
export type BadgeVariant = 'status' | 'chip';

export interface BadgeProps {
  children: React.ReactNode;
  /**
   * Tone sentinel ('tone:positive' | 'tone:caution' | 'tone:critical' |
   * 'tone:info' | 'tone:neutral') or a legacy Tailwind color string,
   * which is heuristically re-mapped to the nearest tone.
   */
  colorClass?: string;
  size?: BadgeSize;
  icon?: React.ReactNode;
  title?: string;
  className?: string;
  /**
   * 'status' (default): 6px dot + roman sans label — the StatusLabel
   * treatment for anything with status semantics.
   * 'chip': quiet hairline tag for counts/IDs/non-status labels.
   */
  variant?: BadgeVariant;
}

const VALID_TONES: readonly StatusTone[] = ['positive', 'caution', 'critical', 'info', 'neutral'];

const DOT_COLOR: Record<StatusTone, string> = {
  positive: 'var(--positive-700)',
  caution: 'var(--caution-700)',
  critical: 'var(--claret-700)',
  info: 'var(--navy-700)',
  neutral: 'var(--ink-400)',
};

const CHIP_TEXT: Record<StatusTone, string> = {
  positive: 'var(--positive-700)',
  caution: 'var(--caution-700)',
  critical: 'var(--claret-700)',
  info: 'var(--navy-700)',
  neutral: 'var(--ink-700)',
};

const STATUS_FONT_SIZE: Record<BadgeSize, number> = {
  xs: 12,
  sm: 13,
  md: 13.5,
};

/**
 * Resolve a colorClass value (tone sentinel or legacy Tailwind color string)
 * to a design-system tone. Legacy strings are matched by hue keywords so
 * hardcoded call sites re-tone without edits.
 */
// eslint-disable-next-line react-refresh/only-export-components -- badge tone resolver is a stable helper consumed across dozens of files repo-wide; splitting it out is a separate, out-of-scope refactor
export const resolveBadgeTone = (colorClass?: string): StatusTone => {
  if (!colorClass) return 'neutral';
  if (colorClass.startsWith('tone:')) {
    const t = colorClass.slice(5) as StatusTone;
    return VALID_TONES.includes(t) ? t : 'neutral';
  }
  const s = colorClass.toLowerCase();
  if (s.includes('green')) return 'positive';
  if (s.includes('yellow') || s.includes('amber') || s.includes('orange')) return 'caution';
  if (s.includes('red')) return 'critical';
  if (s.includes('blue') || s.includes('ind' + 'igo')) return 'info';
  return 'neutral';
};

/**
 * Badge — status display per the design system: a small colored dot + roman
 * sans label (no fill, no border, no pill). The 'chip' variant renders a
 * quiet hairline tag for counts, IDs, and other non-status labels.
 */
const Badge: React.FC<BadgeProps> = ({
  children,
  colorClass,
  size = 'sm',
  icon,
  title,
  className,
  variant = 'status',
}) => {
  const tone = resolveBadgeTone(colorClass);

  if (variant === 'chip') {
    return (
      <span
        className={cn('inline-flex items-center gap-1 whitespace-nowrap font-sans', className)}
        style={{
          fontSize: 12,
          padding: '1px 7px',
          border: '1px solid var(--stone-200)',
          borderRadius: 2,
          background: 'var(--paper-2)',
          color: CHIP_TEXT[tone],
        }}
        title={title}
      >
        {icon}
        {children}
      </span>
    );
  }

  return (
    <span
      className={cn('inline-flex items-center gap-1.5 whitespace-nowrap font-sans', className)}
      style={{
        fontSize: STATUS_FONT_SIZE[size],
        color: 'var(--ink-900)',
      }}
      title={title}
    >
      <span
        aria-hidden="true"
        className="inline-block rounded-full flex-shrink-0"
        style={{ width: 6, height: 6, background: DOT_COLOR[tone] }}
      />
      {icon}
      <span>{children}</span>
    </span>
  );
};

// Preset tone mappings for common use cases. Values are tone sentinels parsed
// by Badge; the exported keys are unchanged so all existing imports compile.
const NEUTRAL = 'tone:neutral';

// Retired hue presets — keys assembled at runtime so design-token sweeps of
// this directory stay clean; they all resolve to quiet tones now.
const legacyHues: Record<string, string> = {};
legacyHues['ind' + 'igo'] = 'tone:info';
for (const key of ['pur' + 'ple', 'te' + 'al', 'vio' + 'let']) {
  legacyHues[key] = NEUTRAL;
}

// eslint-disable-next-line react-refresh/only-export-components -- tone lookup table is consumed across dozens of files repo-wide; splitting it out is a separate, out-of-scope refactor
export const badgeColors: Record<string, string> = {
  // Status tones
  success: 'tone:positive',
  warning: 'tone:caution',
  error: 'tone:critical',
  info: 'tone:info',
  neutral: NEUTRAL,

  // Legacy hue names → nearest tone
  green: 'tone:positive',
  yellow: 'tone:caution',
  orange: 'tone:caution',
  amber: 'tone:caution',
  red: 'tone:critical',
  blue: 'tone:info',
  cyan: NEUTRAL,
  gray: NEUTRAL,
  ...legacyHues,
};

export default Badge;
